import { getI18nService } from '@rendora/common/services/i18n';
import { isUS } from '@rendora/common/utils/config';
import { debounce } from 'lodash-es';
import { computed, onBeforeUnmount, reactive, ref, watch } from 'vue';
import type { IOrder, ISubscription } from '../../../entities/order';
import {
  getOrderClientDetail,
  getOrderClientSubscriptionStatus,
  postOrderClient,
  postOrderClientBuyCreditPackage,
  postOrderClientBuyStorage,
  postOrderClientBuySubAccount,
  postOrderClientSubscriptionCreate,
} from '../../../entities/order';
import {
  BUY_TYPE,
  IRightsType,
  ORDER_BEHAVIOR,
  ORDER_STATUS,
  ORDER_TYPE,
  PAY_TYPE,
  SUBSCRIBE_STATUS,
} from '../../../types/enums';
import { useSingleOrderModal } from '../ui/standalone';
import type { UseOrderExpose, UseOrderInput, UseOrderOptions } from './types';

/**
 * useOrder
 * 订单创建与轮询追踪的可组合函数。
 * - 对外 API：参数与返回结构稳定。
 * - 功能：根据订单类型与支付方式创建订单、设置二维码，并轮询至完成。
 */
export default function useOrder(
  orderBody: Readonly<ReturnType<typeof ref<Partial<UseOrderInput>>>>,
  options: UseOrderOptions = {},
): UseOrderExpose {
  const i18nService = getI18nService();
  const $t = i18nService.t;
  // 轮询间隔
  const POLLING_INTERVAL = options.pollingIntervalMs ?? 3000;

  // 计时器句柄（-1 表示未运行）
  let timer: number = -1;
  // 中断控制器：用于在重新创建订单时取消进行中的请求
  const abort = ref<AbortController>();

  // 基础状态
  const isCreating = ref(false);
  const raw = ref<IOrder | ISubscription>();
  const agreementAccepted = ref(isUS() ? true : false);
  const currentPayType = ref<PAY_TYPE>(PAY_TYPE.ALIPAY);
  const currentRightType = ref<IRightsType>(orderBody.value?.rightsType ?? IRightsType.PERSONAL);

  // 二维码地址
  const qrCode = reactive({
    weixin: '',
    alipay: '',
  });

  // 订单视图状态
  const order = reactive<{ id?: number; status?: ORDER_STATUS; pay_fee?: number }>({});
  const paySucceeded = computed(() => order.status === ORDER_STATUS.PAID);

  // 工具：停止/开启轮询
  function stopPolling(): void {
    clearInterval(timer);
    timer = -1;
  }
  function startPolling(): void {
    if (timer === -1) {
      timer = window.setInterval(getOrder, POLLING_INTERVAL);
    }
  }

  // 工具：按照当前支付方式设置二维码
  function setQrcode(url: string): void {
    if (currentPayType.value === PAY_TYPE.WEIXIN) qrCode.weixin = url;
    if (currentPayType.value === PAY_TYPE.ALIPAY) qrCode.alipay = url;
  }

  // 重新支付：清空状态并重新创建订单
  function repay(): void {
    void create();
  }

  // 轮询订单/订阅状态并更新二维码
  async function getOrder(): Promise<void> {
    if (!order.id) return;

    try {
      const body = orderBody.value as Partial<UseOrderInput> | undefined;
      const isSubscribe = body?.buyType === BUY_TYPE.SUBSCRIBE;

      if (isSubscribe) {
        const res = await getOrderClientSubscriptionStatus(order.id);
        const data = res.data;
        if (!data) return;

        const url = data.order.qr_code_url;
        if (currentPayType.value === PAY_TYPE.ALIPAY) {
          if (url !== qrCode.alipay) qrCode.alipay = url;
        } else {
          if (url !== qrCode.weixin) qrCode.weixin = url;
        }

        if (![ORDER_STATUS.PAYING, ORDER_STATUS.UNPAID].includes(res.data.subscription.status)) {
          if (res.data.subscription.status === SUBSCRIBE_STATUS.SUBSCRIBED) {
            if (res.data.order.id && res.data.subscription.show_qr_code) {
              stopPolling();
              useSingleOrderModal(
                res.data.order,
                res.data.subscription.pay_error === 'ACQ.CYCLE_PAY_TIME_NOT_SUPPORT'
                  ? $t(
                      'gen-ju-zhi-fu-bao-de-xian-zhi-zai-ye-jian-2200-ci-ri-700-wu-fa-jin-hang-lian-xu-bao-yue-kou-fei-qing-sao-ma-wan-cheng-zhi-fu',
                    )
                  : $t(
                      'gen-ju-zhi-fu-bao-de-xian-zhi-lian-xu-bao-yue-tao-can-30-tian-nei-wu-fa-jin-hang-er-ci-kou-fei-wei-bao-zheng-nin-xia-yue-tao-can-an-shi-sheng-xiao-qing-sao-ma-zhi-fu-ben-ci-sheng-ji-fei-yong',
                    ),
              );
            } else if (
              ![ORDER_STATUS.PAYING, ORDER_STATUS.UNPAID].includes(res.data.order.status)
            ) {
              order.status = res.data.order.status;
              order.pay_fee = data.order.pay_fee;
              stopPolling();
            }
          }
        }

        // 当订阅已生效时，同时以订单状态为准
        if (data.subscription.status === SUBSCRIBE_STATUS.SUBSCRIBED) {
          if (![ORDER_STATUS.PAYING, ORDER_STATUS.UNPAID].includes(data.order.status)) {
            order.status = data.order.status;
            stopPolling();
          }
        }
      } else {
        const res = await getOrderClientDetail(order.id);
        const data = res.data;
        if (!data) return;

        const url = data.qr_code_url;
        if (currentPayType.value === PAY_TYPE.ALIPAY) {
          if (url !== qrCode.alipay) qrCode.alipay = url;
        } else {
          if (url !== qrCode.weixin) qrCode.weixin = url;
        }

        if (![ORDER_STATUS.PAYING, ORDER_STATUS.UNPAID].includes(data.status)) {
          order.status = data.status;
          order.pay_fee = data.pay_fee;
          stopPolling();
        }
      }
    } catch {
      // 忽略异常，等待下一次轮询重试
    }
  }

  // 创建订单；支持 isTransfer=true 直接跳转到收银台链接
  async function create(isTransfer?: boolean): Promise<void> {
    try {
      abandon();

      abort.value = new AbortController();
      isCreating.value = true;

      // 协议未勾选时不允许创建
      if (!agreementAccepted.value && !isTransfer) {
        isCreating.value = false;
        return;
      }

      const body = orderBody.value as Partial<UseOrderInput>;
      if (!body) {
        isCreating.value = false;
        return;
      }

      const chosenPayType = isTransfer ? PAY_TYPE.TRANSFER : (body.payType ?? currentPayType.value);
      let res:
        | Awaited<ReturnType<typeof postOrderClientBuyCreditPackage>>
        | Awaited<ReturnType<typeof postOrderClientBuyStorage>>
        | Awaited<ReturnType<typeof postOrderClientBuySubAccount>>
        | Awaited<ReturnType<typeof postOrderClientSubscriptionCreate>>
        | Awaited<ReturnType<typeof postOrderClient>>;

      // 点数（信用币）
      if (body.orderType === ORDER_TYPE.CREDITS) {
        if (!chosenPayType || !body.creditCoinsPackageId) {
          isCreating.value = false;
          return;
        }
        res = await postOrderClientBuyCreditPackage(
          {
            order_type: ORDER_TYPE.CREDITS,
            pay_type: chosenPayType,
            credit_coins_package_id: body.creditCoinsPackageId,
            num: body.num,
          },
          { signal: abort.value.signal },
        );
        // 存储
      } else if (body.orderType === ORDER_TYPE.STORAGE) {
        const isGB = body.isGB ?? body.currentPackageType === 'GB';
        if (!chosenPayType || typeof body.num !== 'number') {
          isCreating.value = false;
          return;
        }
        res = await postOrderClientBuyStorage(
          {
            order_type: ORDER_TYPE.STORAGE,
            pay_type: chosenPayType,
            num: body.num,
            is_GB: Boolean(isGB),
          },
          { signal: abort.value.signal },
        );
        // 加购子账号（席位）
      } else if (body.orderType === ORDER_TYPE.ACCOUNT) {
        if (!chosenPayType || typeof body.num !== 'number') {
          isCreating.value = false;
          return;
        }
        res = await postOrderClientBuySubAccount(
          {
            pay_type: chosenPayType,
            num: body.num,
          },
          { signal: abort.value.signal },
        );
        // 订阅
      } else if (body.buyType === BUY_TYPE.SUBSCRIBE) {
        if (!body.rightsId) {
          isCreating.value = false;
          return;
        }
        res = await postOrderClientSubscriptionCreate(
          {
            rights_id: Number(body.rightsId),
            behavior: body.behavior ?? ORDER_BEHAVIOR.BUY,
          },
          { signal: abort.value.signal },
        );
        // 套餐类订单（一次性权益购买）
      } else {
        if (
          body.rightsType === undefined ||
          body.behavior === undefined ||
          !body.timeIdentity ||
          !chosenPayType
        ) {
          isCreating.value = false;
          return;
        }
        res = await postOrderClient(
          {
            pay_type: chosenPayType,
            rights_type: body.rightsType,
            behavior: body.behavior,
            time_identity: body.timeIdentity,
          },
          { signal: abort.value.signal },
        );
      }

      const data = res.data as IOrder | ISubscription | undefined;

      if (!data) {
        isCreating.value = false;
        return;
      }

      raw.value = data;
      order.id = data.id;

      const paymentUrl =
        (data as ISubscription).subscription_url ||
        (data as IOrder).qr_code_url ||
        (data as IOrder).vendor_redirect_url;

      // 转账支付：直接跳转收银台
      if (isTransfer) {
        if (paymentUrl) window.open(paymentUrl, '_blank');
        isCreating.value = false;
        return;
      }

      if (paymentUrl) setQrcode(paymentUrl);
      startPolling();
    } catch (error) {
      console.debug('create error', error);
    } finally {
      isCreating.value = false;
    }
  }

  // 更换支付方式：清空二维码并通过重新创建订单实现
  function changePayType(next: PAY_TYPE): void {
    abandon();
    currentPayType.value = next;
    void create();
  }

  // 别名：对外仅暴露 setPayType，内部复用 changePayType
  function setPayType(next: PAY_TYPE): void {
    changePayType(next);
  }

  function abandon() {
    stopPolling();
    if (abort.value) abort.value.abort();
    order.status = undefined;
    raw.value = undefined;
    qrCode.weixin = '';
    qrCode.alipay = '';
  }

  // 防抖，避免频繁切换支付方式触发多次创建
  const debounceChangePayType = debounce((next: PAY_TYPE) => changePayType(next), 500);

  // 支付方式切换时：非订阅场景触发重新创建
  watch(
    () => currentPayType.value,
    next => {
      const body = orderBody.value as Partial<UseOrderInput> | undefined;
      if (body?.buyType !== BUY_TYPE.SUBSCRIBE) {
        isCreating.value = true;
        debounceChangePayType(next);
      }
    },
  );

  // 入参变更时：重置二维码并重新创建
  watch(
    () => orderBody.value,
    () => {
      void create();
    },
  );

  // 协议勾选状态变化：
  // - 勾选后：若尚无二维码则创建订单，否则继续轮询
  // - 取消勾选：停止轮询
  watch(
    () => agreementAccepted.value,
    val => {
      if (val) void create();
    },
  );

  // 支付成功后停止轮询
  watch(
    () => paySucceeded.value,
    success => {
      if (success) stopPolling();
    },
  );

  // 组件卸载时清理资源
  onBeforeUnmount(() => {
    if (abort.value) abort.value.abort();
    stopPolling();
  });

  return {
    agreementAccepted,
    currentPayType,
    currentRightType,
    qrCode,
    order,
    raw,
    paySucceeded,
    isCreating,
    abandon,
    repay,
    create,
    setPayType,
  };
}
